import {
  useEffect,
  useRef,
  useState,
  ChangeEvent,
  Fragment,
  MouseEvent,
  ReactNode,
} from "react";
import { HiX, HiChevronLeft } from "react-icons/hi";
import {
  TbBackpack,
  TbCoin,
  TbFilter,
  TbCrosshair,
  TbLayoutDashboard,
  TbLayoutNavbar,
  TbPin,
  TbRadar2,
  TbSkull,
} from "react-icons/tb";

import { analytics } from "MVVM/Analytics";
import cs2Logo from "MVVM/Assets/GameLogos/cs2.svg";
import maskHealthAmmo from "MVVM/Assets/Cs2Hud/health_ammo.png";
import maskMoney from "MVVM/Assets/Cs2Hud/money.png";
import maskEquipment from "MVVM/Assets/Cs2Hud/equipment.png";
import { ICs2Hud, TCs2CrosshairMode } from "MVVM/@types/user";
import { useCs2Hud, CS2_HUD_COLORS } from "MVVM/Hooks/useCs2Hud";
import {
  AllSettingsLink,
  Select,
  SectionIntro,
  CloseButton,
  CodeInput,
  CodeField,
  CodeHint,
  Divider,
  SectionHeader,
  Toggle,
  ToggleKnob,
  ColorGrid,
  ColorSwatch,
  Controls,
  Footer,
  GameLogo,
  Header,
  HeaderText,
  Layout,
  ModalContainer,
  ModalOverlay,
  NavItem,
  OptionRow,
  PresetButton,
  PresetRow,
  Preview,
  PreviewColumn,
  PreviewLayer,
  PreviewNote,
  PreviewStage,
  PreviewTint,
  RadioDot,
  SaveButton,
  SaveFeedback,
  SettingDescription,
  SettingInfo,
  SettingLabel,
  SettingRow,
  TabBar,
  Subtitle,
  Title,
} from "./styledComponents";

interface ICs2HudSettingsModal {
  onClose: () => void;
  onAllSettings?: () => void;
}

type TSection = "HUD" | "CROSSHAIR";

const SECTIONS: { id: TSection; label: string; icon: ReactNode }[] = [
  { id: "HUD", label: "HUD", icon: <TbLayoutDashboard size={18} /> },
  { id: "CROSSHAIR", label: "Crosshair", icon: <TbCrosshair size={18} /> },
];

const CROSSHAIR_MODES: {
  id: TCs2CrosshairMode;
  label: string;
  description: string;
}[] = [
  {
    id: "PLAYER",
    label: "My crosshair",
    description: "Your in-game crosshair, pulled from the demo",
  },
  {
    id: "ALLSTAR",
    label: "Allstar crosshair",
    description: "Our clean default, the same on every clip you make",
  },
  {
    id: "CODE",
    label: "Custom crosshair",
    description: "Paste your CS2 share code",
  },
  {
    id: "HIDDEN",
    label: "No crosshair",
    description: "No crosshair at all on your clips",
  },
];

const PREVIEW_ZOOM = 2.4;

const SHOTS = `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/customization`;
const HUD_ON = `${SHOTS}/hud_all.png`;
const HUD_OFF = `${SHOTS}/hud_disabled.png`;
const HUD_TOP_EXPANDED = `${SHOTS}/hud_top_expanded.png`;

// clip-path inset(), as top/right/bottom/left percentages of the 1920x1080 frame.
// All three shots share one camera, so a clipped layer swaps only its own element.
const REGIONS = {
  radar: "0.8% 83.6% 69.2% 1.1%",
  money: "91.3% 87.6% 0% 0.9%",
  equipment: "67.6% 0% 11.5% 88.7%",
  chat: "70.9% 75.2% 5.9% 0%",
  killfeed: "5.1% 0% 89.7% 74.2%",
  healthAmmo: "90.1% 26.2% 0% 29.8%",
  topCollapsed: "0% 44% 90.9% 42%",
  topExpanded: "0% 30.9% 84.6% 32%",
};

// Presets only touch the HUD elements. Color and kill feed are left alone,
// kill feed because it applies with the HUD off.
const HUD_PRESETS: { label: string; values: Partial<ICs2Hud> }[] = [
  {
    label: "Minimal",
    values: {
      showHud: true,
      topPanel: "COLLAPSED",
      radar: false,
      money: false,
      equipment: false,
      chat: false,
    },
  },
  {
    label: "Everything",
    values: {
      showHud: true,
      topPanel: "EXPANDED",
      radar: true,
      money: true,
      equipment: true,
      chat: true,
    },
  },
];

interface IElement {
  keys: (keyof ICs2Hud)[];
  label: string;
  icon: ReactNode;
  description?: string;
  alwaysEnabled?: boolean;
  dependsOn?: keyof ICs2Hud; // greyed out unless this one is on
}

// Kill feed sits on its own above the HUD sections, since it applies either way.
const KILLFEED: IElement = {
  keys: ["killfeed"],
  label: "Show kill feed",
  icon: <TbSkull size={18} />,
  alwaysEnabled: true,
};

const KILLFEED_OPTIONS: IElement[] = [
  {
    keys: ["onlyMyKills"],
    label: "Only my kills",
    icon: <TbFilter size={18} />,
    description: "Hides kills you were not involved in",
    dependsOn: "killfeed",
  },
  {
    keys: ["pinnedKillfeed"],
    label: "Keep on screen",
    icon: <TbPin size={18} />,
    description: "Entries stay up instead of fading after a few seconds",
    dependsOn: "killfeed",
  },
];

const ELEMENTS: IElement[] = [
  { keys: ["radar"], label: "Radar", icon: <TbRadar2 size={18} /> },
  {
    keys: ["money", "chat"],
    label: "Money & chat",
    icon: <TbCoin size={18} />,
    description: "The game draws these in one panel, so they toggle together",
  },
  { keys: ["equipment"], label: "Equipment", icon: <TbBackpack size={18} /> },
];

export const Cs2HudSettingsModal = ({
  onClose,
  onAllSettings,
}: ICs2HudSettingsModal) => {
  const {
    draft,
    setDraft,
    crosshair,
    setCrosshair,
    isDirty,
    saving,
    saveStatus,
    save,
    isLoggedIn,
  } = useCs2Hud();
  const [section, setSection] = useState<TSection>("HUD");
  const stage = useRef<HTMLDivElement>(null);

  useEffect(() => {
    analytics.track("CS2 HUD - Modal Opened");
  }, []);

  // Magnify toward the cursor. Written straight to the node so moving the mouse
  // does not re-render the modal.
  const zoomToCursor = (e: MouseEvent<HTMLDivElement>) => {
    if (!stage.current) return;

    const box = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - box.left) / box.width) * 100;
    const y = ((e.clientY - box.top) / box.height) * 100;
    stage.current.style.transformOrigin = `${x}% ${y}%`;
    stage.current.style.transform = `scale(${PREVIEW_ZOOM})`;
  };

  const clearZoom = () => {
    if (stage.current) stage.current.style.transform = "";
  };

  const tint = draft.color > 0 ? CS2_HUD_COLORS[draft.color]?.swatch : null;

  const elementRow = (element: IElement) => {
    const disabled = element.dependsOn
      ? !draft[element.dependsOn]
      : !draft.showHud && !element.alwaysEnabled;
    const on = element.keys.every((key) => Boolean(draft[key]));

    const toggle = () => {
      if (disabled) return;

      const patch = element.keys.reduce(
        (acc, key) => ({ ...acc, [key]: !on }),
        {} as Partial<ICs2Hud>,
      );
      setDraft({ ...draft, ...patch });
    };

    return (
      <SettingRow key={element.label} $disabled={disabled}>
        <SettingInfo>
          <SettingLabel>
            {element.icon}
            {element.label}
          </SettingLabel>
          {element.description && (
            <SettingDescription>{element.description}</SettingDescription>
          )}
        </SettingInfo>
        <Toggle
          role="switch"
          aria-checked={on}
          aria-label={element.label}
          $on={on}
          onClick={toggle}
        >
          <ToggleKnob $on={on} />
        </Toggle>
      </SettingRow>
    );
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e: MouseEvent) => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="Close">
          <HiX size={20} />
        </CloseButton>

        <Header>
          <GameLogo src={cs2Logo} alt="Counter-Strike" />
          <HeaderText>
            <Title>Counter-Strike Clip Settings</Title>
            <Subtitle>Applies to every Counter-Strike clip you create</Subtitle>
          </HeaderText>
        </Header>

        <TabBar>
          {SECTIONS.map((item) => (
            <NavItem
              key={item.id}
              type="button"
              $active={section === item.id}
              onClick={() => setSection(item.id)}
            >
              {item.icon}
              {item.label}
            </NavItem>
          ))}
        </TabBar>

        <Layout>
          {section === "HUD" && (
            <PreviewColumn>
              <Preview onMouseMove={zoomToCursor} onMouseLeave={clearZoom}>
                <PreviewStage ref={stage}>
                  <PreviewLayer
                    src={HUD_OFF}
                    alt="Counter-Strike clip preview"
                  />
                  {draft.showHud && (
                    <>
                      <PreviewLayer
                        src={HUD_ON}
                        $clip={REGIONS.healthAmmo}
                        alt=""
                      />
                      {draft.topPanel === "COLLAPSED" && (
                        <PreviewLayer
                          src={HUD_ON}
                          $clip={REGIONS.topCollapsed}
                          alt=""
                        />
                      )}
                      {draft.topPanel === "EXPANDED" && (
                        <PreviewLayer
                          src={HUD_TOP_EXPANDED}
                          $clip={REGIONS.topExpanded}
                          alt=""
                        />
                      )}
                      {draft.radar && (
                        <PreviewLayer
                          src={HUD_ON}
                          $clip={REGIONS.radar}
                          alt=""
                        />
                      )}
                      {draft.money && (
                        <PreviewLayer
                          src={HUD_ON}
                          $clip={REGIONS.money}
                          alt=""
                        />
                      )}
                      {draft.equipment && (
                        <PreviewLayer
                          src={HUD_ON}
                          $clip={REGIONS.equipment}
                          alt=""
                        />
                      )}
                      {draft.chat && (
                        <PreviewLayer
                          src={HUD_ON}
                          $clip={REGIONS.chat}
                          alt=""
                        />
                      )}

                      {/* recolours only the hud pixels, via an alpha mask cut
                          from the difference between the two screenshots. the
                          top panel is team coloured, so it is left alone */}
                      {tint && (
                        <>
                          <PreviewTint
                            $mask={maskHealthAmmo}
                            $inset={REGIONS.healthAmmo}
                            style={{ color: tint }}
                          />
                          {draft.money && (
                            <PreviewTint
                              $mask={maskMoney}
                              $inset={REGIONS.money}
                              style={{ color: tint }}
                            />
                          )}
                          {draft.equipment && (
                            <PreviewTint
                              $mask={maskEquipment}
                              $inset={REGIONS.equipment}
                              style={{ color: tint }}
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                  {draft.killfeed && (
                    <PreviewLayer
                      src={HUD_ON}
                      $clip={REGIONS.killfeed}
                      alt=""
                    />
                  )}
                </PreviewStage>
              </Preview>
              <PreviewNote>
                Preview image. An approximation of your settings, not a 1:1
                match for your clip. Hover to zoom in.
              </PreviewNote>
            </PreviewColumn>
          )}

          <Controls>
            {section === "HUD" && (
              <>
                <SectionHeader>Display</SectionHeader>
                <SettingRow>
                  <SettingInfo>
                    <SettingLabel>
                      <TbLayoutDashboard size={18} />
                      Show HUD elements
                    </SettingLabel>
                  </SettingInfo>
                  <Toggle
                    role="switch"
                    aria-checked={draft.showHud}
                    aria-label="Show HUD elements"
                    $on={draft.showHud}
                    onClick={() =>
                      setDraft({ ...draft, showHud: !draft.showHud })
                    }
                  >
                    <ToggleKnob $on={draft.showHud} />
                  </Toggle>
                </SettingRow>

                <SectionIntro>
                  Presets are a quick starting point. You can still change
                  anything below
                </SectionIntro>
                <PresetRow>
                  {HUD_PRESETS.map((preset) => (
                    <PresetButton
                      key={preset.label}
                      type="button"
                      onClick={() => setDraft({ ...draft, ...preset.values })}
                    >
                      {preset.label}
                    </PresetButton>
                  ))}
                </PresetRow>

                <Divider />

                <SectionHeader>Kill feed</SectionHeader>
                <SectionIntro>
                  Applies even with the HUD turned off
                </SectionIntro>
                {elementRow(KILLFEED)}
                {KILLFEED_OPTIONS.map(elementRow)}

                <Divider />

                <SectionHeader>HUD color</SectionHeader>
                <SectionIntro>Tints your health, ammo and panels</SectionIntro>
                <ColorGrid $disabled={!draft.showHud}>
                  {CS2_HUD_COLORS.map((color, index) => (
                    <ColorSwatch
                      key={color.name}
                      type="button"
                      aria-label={color.name}
                      aria-pressed={draft.color === index}
                      title={color.name}
                      $swatch={color.swatch}
                      $selected={draft.color === index}
                      disabled={!draft.showHud}
                      onClick={() => setDraft({ ...draft, color: index })}
                    />
                  ))}
                </ColorGrid>

                <Divider />

                <SectionHeader>Elements</SectionHeader>
                <SectionIntro>
                  Pick which pieces of the HUD show on your clips
                </SectionIntro>

                <SettingRow $disabled={!draft.showHud}>
                  <SettingInfo>
                    <SettingLabel>
                      <TbLayoutNavbar size={18} />
                      Top panel
                    </SettingLabel>
                  </SettingInfo>
                  <Select
                    aria-label="Top panel"
                    value={draft.topPanel}
                    disabled={!draft.showHud}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      setDraft({
                        ...draft,
                        topPanel: e.target.value as ICs2Hud["topPanel"],
                      })
                    }
                  >
                    <option value="EXPANDED">Expanded</option>
                    <option value="COLLAPSED">Collapsed</option>
                    <option value="HIDDEN">Hidden</option>
                  </Select>
                </SettingRow>

                {ELEMENTS.map(elementRow)}
              </>
            )}

            {section === "CROSSHAIR" && (
              <>
                <SectionHeader>Crosshair</SectionHeader>
                <SectionIntro>
                  Choose which crosshair is drawn on your Counter-Strike clips
                </SectionIntro>

                {CROSSHAIR_MODES.map((mode) => (
                  <Fragment key={mode.id}>
                    <OptionRow
                      type="button"
                      $selected={crosshair.mode === mode.id}
                      onClick={() =>
                        setCrosshair({ ...crosshair, mode: mode.id })
                      }
                    >
                      <RadioDot
                        data-radio
                        $selected={crosshair.mode === mode.id}
                      />
                      <SettingInfo>
                        <SettingLabel>{mode.label}</SettingLabel>
                        <SettingDescription>
                          {mode.description}
                        </SettingDescription>
                      </SettingInfo>
                    </OptionRow>

                    {mode.id === "CODE" && crosshair.mode === "CODE" && (
                      <CodeField>
                        <CodeInput
                          value={crosshair.code ?? ""}
                          placeholder="CSGO-xxxxx-xxxxx-xxxxx-xxxxx-xxxxx"
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setCrosshair({ ...crosshair, code: e.target.value })
                          }
                        />
                        <CodeHint>
                          In CS2, go to Settings, Crosshair, then Share to copy
                          your code
                        </CodeHint>
                      </CodeField>
                    )}
                  </Fragment>
                ))}
              </>
            )}
          </Controls>
        </Layout>

        {isLoggedIn && (
          <Footer>
            {onAllSettings && (
              <AllSettingsLink onClick={onAllSettings}>
                <HiChevronLeft size={14} />
                All Clipping Settings
              </AllSettingsLink>
            )}
            {saveStatus !== "idle" && (
              <SaveFeedback $success={saveStatus === "saved"}>
                {saveStatus === "saved" ? "Saved" : "Failed saving"}
              </SaveFeedback>
            )}
            <SaveButton onClick={save} disabled={saving || !isDirty}>
              {saving
                ? "Saving..."
                : isDirty
                ? "Save Settings"
                : "Already saved"}
            </SaveButton>
          </Footer>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
};
